<template>
  <v-card rounded="0" variant="flat">
    <v-card-text>
      <v-row align="center">
        <v-col class="d-none d-md-flex" cols="auto">
          <v-avatar class="mr-4" color="surface-light" size="90">
            <v-img
              v-if="imgResp.src"
              :aspect-ratio="aspectRatio"
              :sizes="imgResp.sizes"
              :src="imgResp.src"
              :srcset="imgResp.srcset"
              alt="Photo of product occasion"
            />
          </v-avatar>
        </v-col>

        <v-col cols="12" md=""
          ><h5 class="text-h6">
            <span
              class="text-uppercase text-light-blue-darken-2 font-weight-bold"
              >{{ startDateParts?.monthShort }} {{ startDateParts?.day }}</span
            >
            &ndash; {{ name }}
          </h5>

          <div class="text-body-1 font-weight-bold mt-1">
            <template v-if="startDateStr === endDateStr">
              <span>{{ startDateStr }}</span>
              <span> | {{ timeStr }}</span>
            </template>

            <template v-else>
              <span>{{ startDateStr }} – {{ endDateStr }}</span>
              <span> | {{ timeStr }}</span>
            </template>

            <v-btn
              v-if="occasion.schedule"
              class="ml-2"
              color="secondary"
              size="x-small"
              variant="outlined"
              @click="toggleSchedule()"
            >
              {{ isScheduleShown ? 'Hide Schedule' : 'Show Schedule' }}
            </v-btn>
          </div>

          <v-slide-y-transition>
            <v-row v-if="isScheduleShown" class="my-1">
              <v-col>
                <div class="text-body-2 font-weight-bold">
                  Full Class Schedule:
                </div>

                <div
                  v-for="(item, i) in occasion.schedule"
                  :key="i"
                  class="text-body-2"
                >
                  {{ scheduleTimeStr(item) }}
                </div>
              </v-col>
            </v-row>
          </v-slide-y-transition>

          <div v-if="!hideDetails" class="text-body-1">
            {{ description }}
          </div>

          <div>
            <ShareLinkButton
              v-if="!hideShareButton"
              :hash="`#h${occasion.sku}`"
              variant="text"
            />

            <VariantChips
              :info-href="infoHref"
              :variant="occasion.product_variant"
            />

            <v-chip
              v-if="occasion.is_half_day === true"
              class="ml-1 my-1"
              color="red"
              size="small"
            >
              Half Day</v-chip
            >
            <v-chip
              v-if="occasion.is_weekend === true"
              class="ml-1 my-1"
              color="red"
              size="small"
            >
              Weekend</v-chip
            >
          </div>
        </v-col>

        <v-col class="text-h5 text-right font-weight-medium" cols="6" md="auto"
          ><div class="mx-4" style="width: 120px">{{ listPriceStr }}</div>
        </v-col>

        <v-col class="text-center" cols="6" md="auto">
          <v-btn
            v-if="registerAsLink"
            :append-icon="mdiArrowRight"
            :href="registerHref"
            color="primary"
            target="_blank"
            tile
            variant="flat"
            >Add to Cart</v-btn
          >
          <v-btn
            v-else
            color="primary"
            tile
            @click="
              cartBus.emit({
                eventName: 'item_in_list_add',
                price: occasion.list_price_usd,
                priceId: occasion.stripe_list_price_id,
                sku: occasion.sku,
                type: 'hd'
              })
            "
            >Add to Cart</v-btn
          ></v-col
        >
      </v-row>

      <v-row v-if="showEmbedCode">
        <v-col cols="12"><OccasionEmbedCode :occasion="occasion" /></v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'
import { DateTime } from 'luxon'
import { useEventBus, useToggle } from '@vueuse/core'
import { useImgResponsive } from '#root/lib/img'
import { usePageContext } from '#root/renderer/usePageContext'
import { useOccasion } from '#root/lib/useOccasion'
import type { OccasionFeedItem } from '#root/types/occasions'
import { useOccasionHrefs } from '#root/lib/useOccasionHrefs'
import { mdiArrowRight } from '@mdi/js'

const props = withDefaults(
  defineProps<{
    hideDetails?: boolean
    hideInfoButton?: boolean
    hideShareButton?: boolean
    registerAsLink?: boolean
    showEmbedCode?: boolean
    value: OccasionFeedItem
  }>(),
  {
    hideDetails: false,
    hideInfoButton: false,
    hideShareButton: false,
    registerAsLink: false,
    showEmbedCode: false
  }
)

const { canonicalPaths } = usePageContext()
const occasion = toRef(() => props.value)
const {
  description,
  image,
  listPriceStr,
  name,
  startDateStr,
  startDateTime,
  endDateStr,
  endDateTime,
  scheduleTimeStr,
  timeStr
} = useOccasion(occasion)
const { infoHref, registerHref } = useOccasionHrefs(occasion, canonicalPaths)

const startDateParts = computed(() => {
  const dt = startDateTime.value
  return DateTime.isDateTime(dt)
    ? { monthShort: dt.monthShort, day: dt.day }
    : null
})

const aspectRatio = 1
const imgResp = useImgResponsive(
  toRef(() => ({
    aspectRatio,
    image: image.value,
    sizes: {
      xs: '200px'
    }
  }))
)

const [isScheduleShown, toggleSchedule] = useToggle(false)
const cartBus = useEventBus('cart')

defineExpose({ startDateStr, startDateTime, endDateStr, endDateTime })
</script>
